import React, { useState, useEffect, useRef } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Link,
  Grid,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ArrowForward as ArrowForwardIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
  KeyboardArrowDown as ChevronDownIcon,
  VerifiedUser as VerifiedIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import SEOHead from '../../components/SEO/SEOHead';
import BreadcrumbNav from '../../components/SEO/BreadcrumbNav';
import NewsletterSection from '../../components/Layout/NewsletterSection';
import TrustStrip from '../../components/Layout/TrustStrip';
import RelatedArticles from '../../components/Blog/RelatedArticles';
import CrossLinks from '../../components/Layout/CrossLinks';
import ReferralCTA from '../../components/shared/ReferralCTA';
import { useTranslation } from 'react-i18next';

/* ─── TaxKeyNumbers ──────────────────────────────────────────────── */
const TaxKeyNumbers = ({ t }) => {
  const KEY_NUMBERS = [
    {
      box: 'Box 14',
      title: t('blog.t4Explicado.box14Title'),
      value: '$52,000',
      description: t('blog.t4Explicado.box14Desc'),
      accent: '#1E3A5F',
    },
    {
      box: 'Box 22',
      title: t('blog.t4Explicado.box22Title'),
      value: '$8,940',
      description: t('blog.t4Explicado.box22Desc'),
      accent: '#2E7D32',
    },
    {
      box: 'Box 18',
      title: t('blog.t4Explicado.box18Title'),
      value: '$1,002',
      description: t('blog.t4Explicado.box18Desc'),
      accent: '#5F6B7A',
    },
  ];

  return (
    <Box>
      <Grid container spacing={3}>
        {KEY_NUMBERS.map((item) => (
          <Grid item xs={12} sm={4} key={item.box}>
            <Box
              sx={{
                bgcolor: '#FFFFFF',
                border: '1px solid #EEF0F3',
                borderRadius: '16px',
                p: { xs: 3, md: 4 },
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'box-shadow 0.2s ease',
                '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.06)' },
              }}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: item.accent,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  mb: 1,
                }}
              >
                {item.box}
              </Typography>
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#1A1A1A',
                  mb: 1.5,
                }}
              >
                {item.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '28px', md: '32px' },
                  fontWeight: 700,
                  color: item.accent,
                  fontFeatureSettings: "'tnum'",
                  letterSpacing: '-0.02em',
                  mb: 1,
                }}
              >
                {item.value}
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: '#5F6B7A',
                  lineHeight: 1.5,
                  mt: 'auto',
                }}
              >
                {item.description}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Insight bar */}
      <Box
        sx={{
          mt: 3,
          bgcolor: '#E8F5E9',
          border: '1px solid rgba(46,125,50,0.15)',
          borderRadius: '12px',
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20, flexShrink: 0 }} />
        <Typography sx={{ fontSize: '14px', color: '#1A1A1A', lineHeight: 1.6 }}>
          {t('blog.t4Explicado.insightBar')}
        </Typography>
      </Box>
    </Box>
  );
};

/* ─── BoxDetailCard (expandable) ─────────────────────────────────── */
const BoxDetailCard = ({ box, name, desc, impact, impactLabel }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box
      sx={{
        border: '1px solid #EEF0F3',
        borderRadius: '16px',
        overflow: 'hidden',
        bgcolor: '#FFFFFF',
        transition: 'box-shadow 0.2s ease',
        '&:hover': { boxShadow: '0 2px 12px rgba(0,0,0,0.04)' },
      }}
    >
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2.5,
          cursor: 'pointer',
          userSelect: 'none',
        }}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(!open); } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 700,
              color: '#1E3A5F',
              bgcolor: 'rgba(30,58,95,0.08)',
              px: 1.5,
              py: 0.5,
              borderRadius: '8px',
              flexShrink: 0,
              fontFeatureSettings: "'tnum'",
            }}
          >
            {box}
          </Typography>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1A1A1A' }}>
              {name}
            </Typography>
            <Typography sx={{ fontSize: '13px', color: '#5F6B7A', display: { xs: 'none', sm: 'block' } }}>
              {desc}
            </Typography>
          </Box>
        </Box>
        <ChevronDownIcon
          sx={{
            color: '#5F6B7A',
            fontSize: 20,
            transform: open ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.2s ease',
            flexShrink: 0,
          }}
        />
      </Box>
      <Box sx={{ display: open ? 'block' : 'none' }}>
        <Box sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Typography sx={{ fontSize: '13px', color: '#5F6B7A', display: { sm: 'none' }, mb: 1 }}>
            {desc}
          </Typography>
          <Box
            sx={{
              bgcolor: '#F7F9FC',
              borderRadius: '10px',
              px: 2.5,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <ArrowForwardIcon sx={{ color: '#1E3A5F', fontSize: 16 }} />
            <Typography sx={{ fontSize: '14px', color: '#1A1A1A', fontWeight: 500 }}>
              {impactLabel} {impact}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

/* ─── QuebecDualSystem ───────────────────────────────────────────── */
const QuebecDualSystem = ({ t, lng }) => (
  <Box>
    <Grid container spacing={3}>
      {/* Federal */}
      <Grid item xs={12} sm={6}>
        <Box
          sx={{
            bgcolor: '#FFFFFF',
            border: '1px solid #EEF0F3',
            borderRadius: '16px',
            p: { xs: 3, md: 4 },
            height: '100%',
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#1E3A5F',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              mb: 1.5,
            }}
          >
            Federal
          </Typography>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#1A1A1A', mb: 0.5 }}>
            CRA
          </Typography>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1E3A5F', mb: 2 }}>
            {t('blog.t4Explicado.quebecDocT4')}
          </Typography>
          <Typography sx={{ fontSize: '14px', color: '#5F6B7A', lineHeight: 1.7 }}>
            {t('blog.t4Explicado.quebecFederalDesc')}
          </Typography>
        </Box>
      </Grid>

      {/* Provincial */}
      <Grid item xs={12} sm={6}>
        <Box
          sx={{
            bgcolor: '#FFFFFF',
            border: '1px solid #EEF0F3',
            borderRadius: '16px',
            p: { xs: 3, md: 4 },
            height: '100%',
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#5F6B7A',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              mb: 1.5,
            }}
          >
            Provincial (Quebec)
          </Typography>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#1A1A1A', mb: 0.5 }}>
            Revenu Qu&eacute;bec
          </Typography>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#5F6B7A', mb: 2 }}>
            {t('blog.t4Explicado.quebecDocRL1')}
          </Typography>
          <Typography sx={{ fontSize: '14px', color: '#5F6B7A', lineHeight: 1.7 }}>
            {t('blog.t4Explicado.quebecProvincialDesc')}
          </Typography>
        </Box>
      </Grid>
    </Grid>

    {/* Info bar */}
    <Box
      sx={{
        mt: 3,
        bgcolor: '#F7F9FC',
        border: '1px solid #EEF0F3',
        borderRadius: '12px',
        px: 3,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <Typography sx={{ fontSize: '14px', color: '#1A1A1A', lineHeight: 1.6 }}>
        {t('blog.t4Explicado.quebecInfoBar1')}{' '}
        <strong>{t('blog.t4Explicado.quebecInfoBarBold')}</strong>{' '}
        {t('blog.t4Explicado.quebecInfoBar2')}{' '}
        <RouterLink to={`/${lng}/blog/guia-impuestos-latinos-quebec-2026`} style={{ color: '#1E3A5F', textDecoration: 'underline' }}>
          {t('blog.t4Explicado.quebecInfoBarLink')}
        </RouterLink>.
      </Typography>
    </Box>
  </Box>
);

/* ─── FAQCardGroup ───────────────────────────────────────────────── */
const FAQCardGroup = ({ faqs }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    {faqs.map((faq, i) => (
      <FAQCard key={i} question={faq.q} answer={faq.a} />
    ))}
  </Box>
);

const FAQCard = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box
      sx={{
        border: '1px solid #EEF0F3',
        borderRadius: '16px',
        bgcolor: '#FFFFFF',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s ease',
        '&:hover': { boxShadow: '0 2px 12px rgba(0,0,0,0.04)' },
      }}
    >
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2, md: 2.5 },
          cursor: 'pointer',
          userSelect: 'none',
        }}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(!open); } }}
      >
        <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1A1A1A', pr: 2 }}>
          {question}
        </Typography>
        <ChevronDownIcon
          sx={{
            color: '#5F6B7A',
            fontSize: 20,
            transform: open ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.2s ease',
            flexShrink: 0,
          }}
        />
      </Box>
      <Box sx={{ display: open ? 'block' : 'none' }}>
        <Box sx={{ px: { xs: 2.5, md: 3 }, pb: { xs: 2, md: 2.5 }, pt: 0 }}>
          <Typography sx={{ fontSize: '14px', color: '#5F6B7A', lineHeight: 1.7 }}>
            {answer}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

/* ─── Floating Side Nav (desktop) ────────────────────────────────── */
const SideNav = ({ t }) => {
  const [active, setActive] = useState('');

  const SIDE_NAV_ITEMS = [
    { id: 'que-es', label: t('blog.t4Explicado.navQueEs') },
    { id: 'como-leerlo', label: t('blog.t4Explicado.navComoLeerlo') },
    { id: 'refund', label: t('blog.t4Explicado.navRefund') },
    { id: 'quebec', label: 'Quebec' },
    { id: 'si-no-llega', label: t('blog.t4Explicado.navSiNoLlega') },
    { id: 'faq', label: 'FAQ' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      for (let i = SIDE_NAV_ITEMS.length - 1; i >= 0; i--) {
        const el = document.getElementById(SIDE_NAV_ITEMS[i].id);
        if (el && el.getBoundingClientRect().top <= 120) {
          setActive(SIDE_NAV_ITEMS[i].id);
          return;
        }
      }
      setActive('');
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Box
      component="nav"
      aria-label={t('blog.t4Explicado.navAriaLabel')}
      sx={{
        position: 'sticky',
        top: 100,
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        gap: 0.5,
        width: 140,
        flexShrink: 0,
      }}
    >
      {SIDE_NAV_ITEMS.map((item) => (
        <Typography
          key={item.id}
          component="a"
          href={`#${item.id}`}
          onClick={(e) => {
            e.preventDefault();
            document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
          }}
          sx={{
            fontSize: '12px',
            fontWeight: active === item.id ? 600 : 400,
            color: active === item.id ? '#1E3A5F' : '#5F6B7A',
            textDecoration: 'none',
            py: 0.75,
            px: 1.5,
            borderLeft: '2px solid',
            borderColor: active === item.id ? '#1E3A5F' : 'transparent',
            transition: 'all 0.15s ease',
            '&:hover': { color: '#1E3A5F' },
          }}
        >
          {item.label}
        </Typography>
      ))}
    </Box>
  );
};

/* ─── Section wrapper ────────────────────────────────────────────── */
const Section = ({ id, children }) => (
  <Box
    id={id}
    component="section"
    sx={{
      scrollMarginTop: '80px',
      mb: { xs: 8, md: 10 },
    }}
  >
    {children}
  </Box>
);

/* ─── Main Page ──────────────────────────────────────────────────── */
const T4ExplicadoPage = () => {
  const { t, i18n } = useTranslation();
  const { lng } = useParams();

  /* ─── Schema ─────────────────────────────────────────────────────── */
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: t('blog.t4Explicado.seoTitle'),
    description: t('blog.t4Explicado.seoDescription'),
    datePublished: '2026-02-21',
    dateModified: '2026-02-21',
    author: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com' },
    publisher: {
      '@type': 'Organization',
      name: 'FinMaple',
      url: 'https://www.finmaple.com',
      logo: { '@type': 'ImageObject', url: 'https://www.finmaple.com/og-image.jpg' },
    },
    mainEntityOfPage: 'https://www.finmaple.com/blog/t4-explicado-espanol-impuestos-canada',
    inLanguage: i18n.language,
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: t('blog.t4Explicado.faq1Q'), acceptedAnswer: { '@type': 'Answer', text: t('blog.t4Explicado.faq1A') } },
      { '@type': 'Question', name: t('blog.t4Explicado.faq2Q'), acceptedAnswer: { '@type': 'Answer', text: t('blog.t4Explicado.faq2A') } },
      { '@type': 'Question', name: t('blog.t4Explicado.faq3Q'), acceptedAnswer: { '@type': 'Answer', text: t('blog.t4Explicado.faq3A') } },
      { '@type': 'Question', name: t('blog.t4Explicado.faq4Q'), acceptedAnswer: { '@type': 'Answer', text: t('blog.t4Explicado.faq4A') } },
      { '@type': 'Question', name: t('blog.t4Explicado.faq5Q'), acceptedAnswer: { '@type': 'Answer', text: t('blog.t4Explicado.faq5A') } },
    ],
  };

  /* ─── Data ────────────────────────────────────────────────────────── */
  const ALL_BOXES = [
    { box: 'Box 14', name: 'Employment income', desc: t('blog.t4Explicado.allBox14Desc'), impact: t('blog.t4Explicado.allBox14Impact') },
    { box: 'Box 22', name: 'Income tax deducted', desc: t('blog.t4Explicado.allBox22Desc'), impact: t('blog.t4Explicado.allBox22Impact') },
    { box: 'Box 18 y 24', name: 'EI Premiums', desc: t('blog.t4Explicado.allBox18Desc'), impact: t('blog.t4Explicado.allBox18Impact') },
    { box: 'Box 16', name: 'CPP contributions', desc: t('blog.t4Explicado.allBox16Desc'), impact: t('blog.t4Explicado.allBox16Impact') },
    { box: 'Box 20', name: 'RPP Contributions', desc: t('blog.t4Explicado.allBox20Desc'), impact: t('blog.t4Explicado.allBox20Impact') },
    { box: 'Box 46', name: 'Charitable donations', desc: t('blog.t4Explicado.allBox46Desc'), impact: t('blog.t4Explicado.allBox46Impact') },
  ];

  const FAQS = [
    { q: t('blog.t4Explicado.faq1Q'), a: t('blog.t4Explicado.faq1A') },
    { q: t('blog.t4Explicado.faq2Q'), a: t('blog.t4Explicado.faq2A') },
    { q: t('blog.t4Explicado.faq3Q'), a: t('blog.t4Explicado.faq3A') },
    { q: t('blog.t4Explicado.faq4Q'), a: t('blog.t4Explicado.faq4A') },
    { q: t('blog.t4Explicado.faq5Q'), a: t('blog.t4Explicado.faq5A') },
  ];

  const NOT_RECEIVED_STEPS = [
    { text: t('blog.t4Explicado.notReceived1') },
    { text: t('blog.t4Explicado.notReceived2') },
    { text: t('blog.t4Explicado.notReceived3') },
    { text: t('blog.t4Explicado.notReceived4') },
  ];

  return (
    <Box component="article" sx={{ bgcolor: '#FFFFFF' }}>
      <SEOHead
        title={t('blog.t4Explicado.seoTitle')}
        description={t('blog.t4Explicado.seoDescription')}
        canonical="/blog/t4-explicado-espanol-impuestos-canada"
        routeKey="blogT4"
        seoKey="blogT4"
        noIndex={i18n.language !== 'es'}
        ogImage="https://www.finmaple.com/images/blog/t4-formulario-impuestos-canada.jpg"
        schema={[articleSchema, faqSchema]}
      />

      {/* -- 1. Product-style header -- */}
      <Box sx={{ pt: { xs: 5, md: 10 }, pb: { xs: 4, md: 5 } }}>
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 720 }}>
            <BreadcrumbNav items={[{ label: t('blog.t4Explicado.breadcrumbGuides'), to: `/${lng}/blog` }, { label: t('blog.t4Explicado.breadcrumbT4') }]} />

            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontSize: { xs: '32px', md: '48px' },
                fontWeight: 700,
                color: '#1A1A1A',
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                mt: 3,
                mb: 2,
              }}
            >
              {t('blog.t4Explicado.heroTitle')}
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: '17px', md: '19px' },
                color: '#5F6B7A',
                lineHeight: 1.6,
                mb: 3,
                maxWidth: '60ch',
              }}
            >
              {t('blog.t4Explicado.heroSubtitle')}
            </Typography>

            {/* Meta row */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 2, md: 3 }, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <ScheduleIcon sx={{ fontSize: 15, color: '#5F6B7A' }} />
                <Typography sx={{ fontSize: '13px', color: '#5F6B7A' }}>
                  {t('blog.t4Explicado.metaUpdated')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <PersonIcon sx={{ fontSize: 15, color: '#5F6B7A' }} />
                <Typography sx={{ fontSize: '13px', color: '#5F6B7A' }}>
                  {t('blog.t4Explicado.metaReadTime')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <PersonIcon sx={{ fontSize: 15, color: '#5F6B7A' }} />
                <Typography sx={{ fontSize: '13px', color: '#5F6B7A' }}>
                  {t('blog.t4Explicado.metaAuthor')}
                </Typography>
              </Box>
            </Box>

            {/* Verified badge */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'rgba(30,58,95,0.06)',
                border: '1px solid rgba(30,58,95,0.12)',
                borderRadius: '10px',
                px: 2,
                py: 1,
              }}
            >
              <VerifiedIcon sx={{ fontSize: 16, color: '#1E3A5F' }} />
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#1E3A5F' }}>
                {t('blog.t4Explicado.verifiedBadge')}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* -- Content area with side nav -- */}
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            gap: { lg: 8 },
            pb: { xs: 5, md: 10 },
          }}
        >
          {/* Side navigation */}
          <SideNav t={t} />

          {/* Main content column */}
          <Box sx={{ flex: 1, minWidth: 0, maxWidth: 800 }}>

            {/* -- 2. Tax Snapshot -- */}
            <Section id="que-es">
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontSize: { xs: '24px', md: '30px' },
                  fontWeight: 700,
                  color: '#1A1A1A',
                  mb: 1.5,
                }}
              >
                {t('blog.t4Explicado.snapshotTitle')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '17px',
                  color: '#5F6B7A',
                  lineHeight: 1.6,
                  mb: 4,
                  maxWidth: '65ch',
                }}
              >
                {t('blog.t4Explicado.snapshotDesc')}
              </Typography>
              <TaxKeyNumbers t={t} />
            </Section>

            {/* -- 3. Como leerlo -- */}
            <Section id="como-leerlo">
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontSize: { xs: '24px', md: '30px' },
                  fontWeight: 700,
                  color: '#1A1A1A',
                  mb: 1.5,
                }}
              >
                {t('blog.t4Explicado.howToReadTitle')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '17px',
                  color: '#5F6B7A',
                  lineHeight: 1.6,
                  mb: 4,
                  maxWidth: '65ch',
                }}
              >
                {t('blog.t4Explicado.howToReadDesc')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {ALL_BOXES.map((box) => (
                  <BoxDetailCard key={box.box} {...box} impactLabel={t('blog.t4Explicado.impactLabel')} />
                ))}
              </Box>
            </Section>

            {/* -- 4. Refund o pago -- */}
            <Section id="refund">
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontSize: { xs: '24px', md: '30px' },
                  fontWeight: 700,
                  color: '#1A1A1A',
                  mb: 1.5,
                }}
              >
                {t('blog.t4Explicado.refundTitle')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '17px',
                  color: '#5F6B7A',
                  lineHeight: 1.6,
                  mb: 3,
                  maxWidth: '65ch',
                }}
              >
                {t('blog.t4Explicado.refundDesc1')}{' '}
                <RouterLink to={`/${lng}/blog/rrsp-deadline-marzo-2026`} style={{ color: '#1E3A5F', textDecoration: 'underline' }}>
                  {t('blog.t4Explicado.refundRRSPLink')}
                </RouterLink>{' '}
                {t('blog.t4Explicado.refundDesc2')}
              </Typography>

              {/* Refund logic card */}
              <Box
                sx={{
                  bgcolor: '#FFFFFF',
                  border: '1px solid #EEF0F3',
                  borderRadius: '16px',
                  p: { xs: 3, md: 4 },
                  mb: 3,
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box
                      sx={{
                        width: 32, height: 32, borderRadius: '10px',
                        bgcolor: '#E8F5E9', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}
                    >
                      <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#2E7D32' }}>+</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
                        {t('blog.t4Explicado.refundPositiveTitle')}
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: '#5F6B7A', lineHeight: 1.6 }}>
                        {t('blog.t4Explicado.refundPositiveDesc')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box
                      sx={{
                        width: 32, height: 32, borderRadius: '10px',
                        bgcolor: '#FFF8E1', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}
                    >
                      <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#F9A825' }}>−</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
                        {t('blog.t4Explicado.refundNegativeTitle')}
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: '#5F6B7A', lineHeight: 1.6 }}>
                        {t('blog.t4Explicado.refundNegativeDesc')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Section>

            {/* -- 5. Quebec -- */}
            <Section id="quebec">
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontSize: { xs: '24px', md: '30px' },
                  fontWeight: 700,
                  color: '#1A1A1A',
                  mb: 1.5,
                }}
              >
                {t('blog.t4Explicado.quebecTitle')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '17px',
                  color: '#5F6B7A',
                  lineHeight: 1.6,
                  mb: 4,
                  maxWidth: '65ch',
                }}
              >
                {t('blog.t4Explicado.quebecDesc1')}{' '}
                <RouterLink to={`/${lng}/blog/cra-vs-revenu-quebec-diferencias`} style={{ color: '#1E3A5F', textDecoration: 'underline' }}>
                  {t('blog.t4Explicado.quebecCRALink')}
                </RouterLink>.
              </Typography>
              <QuebecDualSystem t={t} lng={lng} />
            </Section>

            {/* -- 6. Si no llega -- */}
            <Section id="si-no-llega">
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontSize: { xs: '24px', md: '30px' },
                  fontWeight: 700,
                  color: '#1A1A1A',
                  mb: 1.5,
                }}
              >
                {t('blog.t4Explicado.notReceivedTitle')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '17px',
                  color: '#5F6B7A',
                  lineHeight: 1.6,
                  mb: 4,
                  maxWidth: '65ch',
                }}
              >
                {t('blog.t4Explicado.notReceivedDesc')}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {NOT_RECEIVED_STEPS.map((step, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      bgcolor: '#FFFFFF',
                      border: '1px solid #EEF0F3',
                      borderRadius: '16px',
                      px: 3,
                      py: 2.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        minWidth: 28,
                        borderRadius: '50%',
                        bgcolor: '#1E3A5F',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '13px',
                        fontWeight: 700,
                        flexShrink: 0,
                        mt: 0.25,
                      }}
                    >
                      {i + 1}
                    </Box>
                    <Typography sx={{ fontSize: '16px', color: '#1A1A1A', lineHeight: 1.7 }}>
                      {step.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Section>

            {/* -- 7. FAQ -- */}
            <Section id="faq">
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontSize: { xs: '24px', md: '30px' },
                  fontWeight: 700,
                  color: '#1A1A1A',
                  mb: 1.5,
                }}
              >
                {t('blog.t4Explicado.faqTitle')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '17px',
                  color: '#5F6B7A',
                  lineHeight: 1.6,
                  mb: 4,
                  maxWidth: '65ch',
                }}
              >
                {t('blog.t4Explicado.faqDesc')}
              </Typography>
              <FAQCardGroup faqs={FAQS} />
            </Section>

            {/* -- 8. Bottom: Affiliate cards -- */}
            <Box sx={{ mb: { xs: 8, md: 10 } }}>
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontSize: { xs: '24px', md: '30px' },
                  fontWeight: 700,
                  color: '#1A1A1A',
                  mb: 1.5,
                }}
              >
                {t('blog.t4Explicado.affiliateTitle')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '17px',
                  color: '#5F6B7A',
                  lineHeight: 1.6,
                  mb: 4,
                  maxWidth: '65ch',
                }}
              >
                {t('blog.t4Explicado.affiliateDesc')}
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      bgcolor: '#FFFFFF',
                      border: '1px solid #EEF0F3',
                      borderRadius: '16px',
                      p: { xs: 3, md: 4 },
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'box-shadow 0.2s ease',
                      '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.06)' },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        bgcolor: '#1E3A5F',
                        color: '#fff',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: 600,
                        mb: 2,
                        alignSelf: 'flex-start',
                      }}
                    >
                      {t('blog.t4Explicado.recommended')}
                    </Box>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1A1A1A', mb: 1 }}>
                      Wealthsimple Tax
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#5F6B7A', lineHeight: 1.7, mb: 3, flex: 1 }}>
                      {t('blog.t4Explicado.wealthsimpleDesc')}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#2E7D32' }}>
                      {t('blog.t4Explicado.wealthsimpleTags')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      bgcolor: '#FFFFFF',
                      border: '1px solid #EEF0F3',
                      borderRadius: '16px',
                      p: { xs: 3, md: 4 },
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'box-shadow 0.2s ease',
                      '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.06)' },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        bgcolor: 'rgba(95,107,122,0.1)',
                        color: '#5F6B7A',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: 600,
                        mb: 2,
                        alignSelf: 'flex-start',
                      }}
                    >
                      {t('blog.t4Explicado.freeVersion')}
                    </Box>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1A1A1A', mb: 1 }}>
                      TurboTax Canada
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#5F6B7A', lineHeight: 1.7, mb: 3, flex: 1 }}>
                      {t('blog.t4Explicado.turbotaxDesc')}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#5F6B7A' }}>
                      {t('blog.t4Explicado.turbotaxTags')}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* -- 9. Maya soft assist -- */}
            <Box
              sx={{
                bgcolor: '#F7F9FC',
                border: '1px solid #EEF0F3',
                borderRadius: '16px',
                p: { xs: 3, md: 4 },
                display: 'flex',
                alignItems: 'center',
                gap: 2.5,
                mb: { xs: 5, md: 6 },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  minWidth: 40,
                  borderRadius: '50%',
                  bgcolor: '#1E3A5F',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>M</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
                  {t('blog.t4Explicado.mayaTitle')}
                </Typography>
                <Typography sx={{ fontSize: '14px', color: '#5F6B7A', lineHeight: 1.6 }}>
                  {t('blog.t4Explicado.mayaDesc')}
                </Typography>
              </Box>
            </Box>

            {/* -- 10. Quebec CTA (navy, not red) -- */}
            <Box
              sx={{
                bgcolor: '#1E3A5F',
                borderRadius: '16px',
                p: { xs: 3, md: 4 },
                display: 'flex',
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2.5,
                mb: { xs: 5, md: 6 },
              }}
            >
              <Box>
                <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#FFFFFF', mb: 0.5 }}>
                  {t('blog.t4Explicado.quebecCTATitle')}
                </Typography>
                <Typography sx={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.6 }}>
                  {t('blog.t4Explicado.quebecCTADesc')}
                </Typography>
              </Box>
              <Button
                component={RouterLink}
                to={`/${lng}/blog/solidarity-tax-credit-quebec-guia-completa`}
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  bgcolor: '#FFFFFF',
                  color: '#1E3A5F',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '14px',
                  borderRadius: '12px',
                  px: 3,
                  py: 1.25,
                  flexShrink: 0,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#F7F9FC', boxShadow: 'none' },
                }}
              >
                {t('blog.t4Explicado.quebecCTAButton')}
              </Button>
            </Box>

            {/* -- 11. Related articles -- */}
            <RelatedArticles slug="t4-explicado-espanol-impuestos-canada" />

            {/* -- 12. Official sources -- */}
            <Box
              sx={{
                pt: 4,
                borderTop: '1px solid #EEF0F3',
              }}
            >
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 2 }}>
                {t('blog.t4Explicado.officialSources')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Link
                  href="https://www.canada.ca/en/revenue-agency.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: '#1E3A5F',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Canada Revenue Agency — canada.ca/taxes
                  <OpenInNewIcon sx={{ fontSize: 14, color: '#5F6B7A' }} />
                </Link>
                <Link
                  href="https://www.revenuquebec.ca"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: '#1E3A5F',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Revenu Qu&eacute;bec — revenuquebec.ca
                  <OpenInNewIcon sx={{ fontSize: 14, color: '#5F6B7A' }} />
                </Link>
              </Box>
              <Typography sx={{ fontSize: '12px', color: '#5F6B7A', mt: 2.5 }}>
                {t('blog.t4Explicado.footerUpdated')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Wealthsimple CTA */}
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ bgcolor: '#F7F9FC', borderRadius: '12px', p: { xs: 2.5, md: 3 }, border: '1px solid #EEF0F3', textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#1A1A1A', mb: 0.5 }}>
            {lng === 'fr' ? 'Investissez votre remboursement d\'impôt' : lng === 'en' ? 'Invest your tax refund' : '¿Recibes reembolso? Invierte la diferencia'}
          </Typography>
          <Typography sx={{ fontSize: '14px', color: '#5F6B7A', lineHeight: 1.7, mb: 2 }}>
            {lng === 'fr'
              ? 'Ouvrez un CÉLI ou un REER avec Wealthsimple — sans frais cachés, idéal pour les nouveaux arrivants.'
              : lng === 'en'
              ? 'Open a TFSA or RRSP with Wealthsimple — no hidden fees, ideal for newcomers.'
              : 'Abre una TFSA o RRSP con Wealthsimple — sin comisiones ocultas, ideal para recién llegados.'}
          </Typography>
          <ReferralCTA bank="wealthsimple">
            {lng === 'fr' ? 'Ouvrir un compte Wealthsimple' : lng === 'en' ? 'Open a Wealthsimple account' : 'Abrir cuenta Wealthsimple'}
          </ReferralCTA>
        </Box>
      </Container>

      <Container maxWidth="md" sx={{ pb: 4 }}>
        <CrossLinks
          title={lng === 'fr' ? 'Outils connexes' : lng === 'en' ? 'Related tools' : 'Herramientas relacionadas'}
          links={[
            { label: lng === 'fr' ? 'Calculatrice de salaire net' : lng === 'en' ? 'Net salary calculator' : 'Calculadora salario neto', to: '/herramientas/calculadora-salario-neto' },
            { label: lng === 'fr' ? 'Calendrier fiscal 2026' : lng === 'en' ? 'Tax calendar 2026' : 'Calendario fiscal 2026', to: '/calendario-fiscal-2026' },
            { label: lng === 'fr' ? 'Calculatrice REER vs CÉLI' : lng === 'en' ? 'RRSP vs TFSA calculator' : 'Calculadora RRSP vs TFSA', to: '/herramientas/calculadora-rrsp-vs-tfsa' },
            { label: 'CRA vs Revenu Québec', to: '/blog/cra-vs-revenu-quebec-diferencias' },
          ]}
        />
      </Container>

      <TrustStrip />
      <NewsletterSection />
    </Box>
  );
};

export default T4ExplicadoPage;
